import React, { useRef, useEffect, useLayoutEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { buildUnionPath } from './unionPath'
import { usePlatform } from '../../../hooks/usePlatform'
import { onAppEvent } from '../../../lib/events'
import { computeInactivePillRect, INACTIVE_PILL_HEIGHT } from './inactivePillGeometry'
import './ContentUnionShape.scss'

/** Dauer des Sidebar-Übergangs, während der die gezeichnete Ebene nachgeführt wird. */
const FOLLOW_MS = 300

interface ContentUnionShapeProps {
  contentRef: React.RefObject<HTMLDivElement | null>
  activeTabId?: string
  isFirstTabActive?: boolean
  isPreview?: boolean
  sidebarLeftOpen?: boolean
  sidebarRightOpen?: boolean
  tabCount?: number
  pinnedCount?: number
  /** DOM element at window-frame level (before titlebar) where bump layers are portaled */
  bumpSlotEl?: HTMLDivElement | null
  /** DOM element at window-frame level (after content) where border SVG is portaled */
  borderSlotEl?: HTMLDivElement | null
  /** Compact list window mode. Toggling this swaps the TabBar in/out of the
   *  titlebar slot, so the resize observers must re-attach + re-measure. */
  isListMode?: boolean
}

const ANIM_DURATION = 350 // Tab switch animation in ms

type Rect = { x: number; y: number; w: number; h: number }

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

function lerpRect(a: Rect, b: Rect, t: number): Rect {
  return {
    x: lerp(a.x, b.x, t),
    y: lerp(a.y, b.y, t),
    w: lerp(a.w, b.w, t),
    h: lerp(a.h, b.h, t),
  }
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3)
}

const ContentUnionShapeComponent: React.FC<ContentUnionShapeProps> = ({
  contentRef,
  activeTabId,
  isFirstTabActive = false,
  isPreview = false,
  sidebarLeftOpen,
  sidebarRightOpen,
  tabCount,
  pinnedCount,
  bumpSlotEl,
  borderSlotEl,
  isListMode,
}) => {
  const { isElectron } = usePlatform()
  const bgPathRef = useRef<SVGPathElement>(null)
  const borderPathRef = useRef<SVGPathElement>(null)
  const innerShadowPathRef = useRef<SVGPathElement>(null)
  const innerShadowClipRef = useRef<SVGPathElement>(null)
  const gradientRef = useRef<SVGLinearGradientElement>(null)
  const tabBgPillRef = useRef<HTMLDivElement>(null)
  const inactivePillsRef = useRef<HTMLDivElement>(null)

  // Animation state
  const displayedTabRef = useRef<Rect | null>(null)
  const prevTabIdRef = useRef<string | undefined>(undefined)
  const animFrameRef = useRef<number>(0)
  const isAnimatingRef = useRef(false)
  // While true, inactive pills are created/kept WITHOUT the slide transition —
  // used across a window-mode switch so the union tabs don't slide as the window
  // resizes back (async). Combined with hiding them across the settle, they
  // appear directly at the final position instead of two-stepping in from the left.
  const suppressPillAnimRef = useRef(false)
  const prevListModeRef = useRef(isListMode)
  const isFirstTabRef = useRef(isFirstTabActive)
  isFirstTabRef.current = isFirstTabActive
  const prevTabCountRef = useRef(tabCount)

  // Measure content + tab positions relative to frame.
  // When useLayoutPosition is true, the active tab's position is computed via
  // offsetLeft/offsetTop (immune to CSS transforms such as DnD-Kit FLIP animations).
  // This gives the tab's final layout position even during transform transitions.
  // Default (false) uses getBoundingClientRect which includes CSS transforms —
  // needed during drag so the bump follows the visually-moving tab.
  /**
   * @param opts.frameRect Das Rechteck des Fensterrahmens, wenn es der
   * Aufrufer schon hat. Jedes Lesen eines Rechtecks zwingt den Browser, das
   * Layout nachzurechnen, sobald seither etwas geschrieben wurde — und dieser
   * Rahmen wird in einem Durchgang von zwei Stellen gebraucht
   * (`updateInactiveTabPills` ist die zweite). Einmal lesen und durchreichen
   * spart je Durchgang einen Durchgang; bei einer laufenden Bewegung sind das
   * ein bis zwei je Bild.
   */
  const measure = useCallback((opts?: { useLayoutPosition?: boolean; frameRect?: DOMRect }): {
    content: Rect
    tab: Rect | null
  } | null => {
    const contentEl = contentRef.current
    if (!contentEl) return null

    let frameRect = opts?.frameRect
    if (!frameRect) {
      const frameEl = document.querySelector('.window-frame, .web-frame')
      if (!frameEl) return null
      frameRect = frameEl.getBoundingClientRect()
    }
    const contentRect = contentEl.getBoundingClientRect()

    const content: Rect = {
      x: contentRect.left - frameRect.left,
      y: contentRect.top - frameRect.top,
      w: contentRect.width,
      h: contentRect.height,
    }

    const tabEl = document.querySelector('.tab-bar__tab--active') as HTMLElement | null
    let tab: Rect | null = null

    if (tabEl) {
      if (opts?.useLayoutPosition && tabEl.offsetParent) {
        const parent = tabEl.offsetParent as HTMLElement
        const parentRect = parent.getBoundingClientRect()
        tab = {
          x: parentRect.left - frameRect.left + tabEl.offsetLeft,
          y: parentRect.top - frameRect.top + tabEl.offsetTop,
          w: tabEl.offsetWidth,
          h: tabEl.offsetHeight,
        }
      } else {
        const tabRect = tabEl.getBoundingClientRect()
        tab = {
          x: tabRect.left - frameRect.left,
          y: tabRect.top - frameRect.top,
          w: tabRect.width,
          h: tabRect.height,
        }
      }
    }

    return { content, tab }
  }, [contentRef])

  // Position the tab background pill at the active tab's measured rect.
  // The pill is rendered before the SVG in the bump layer (DOM order),
  // so the bump fill naturally covers it.
  const updateTabBgPill = useCallback((tab: Rect | null) => {
    const el = tabBgPillRef.current
    if (!el) return
    if (!tab) {
      el.style.display = 'none'
      return
    }
    el.style.display = ''
    el.style.left = `${tab.x}px`
    el.style.top = `${tab.y}px`
    el.style.width = `${tab.w}px`
    el.style.height = `${tab.h}px`
  }, [])

  // Sync inactive tab background pills into the bump-slot layer.
  // These render BEFORE the bump SVG (DOM order within the slot), so the bump
  // naturally covers them. This separates tab backgrounds from the tab elements,
  // allowing tabs to have z-index:6 (always above bump) while their backgrounds
  // stay below the bump.
  const updateInactiveTabPills = useCallback((frameRectIn?: DOMRect) => {
    const container = inactivePillsRef.current
    if (!container) return

    let frameRect = frameRectIn
    if (!frameRect) {
      const frameEl = document.querySelector('.window-frame, .web-frame')
      if (!frameEl) return
      frameRect = frameEl.getBoundingClientRect()
    }

    const allTabs = document.querySelectorAll('.tab-bar__tab') as NodeListOf<HTMLElement>

    // Sync pill element count to tab count
    while (container.children.length < allTabs.length) {
      const pill = document.createElement('div')
      pill.className = suppressPillAnimRef.current
        ? 'content-union-shape__inactive-pill'
        : 'content-union-shape__inactive-pill content-union-shape__inactive-pill--animated'
      container.appendChild(pill)
    }
    while (container.children.length > allTabs.length) {
      container.removeChild(container.lastChild!)
    }

    // Pill geometry uses `tab.getBoundingClientRect()` (see
    // `inactivePillGeometry.ts`) so the pill follows DnD-Kit's transforms
    // during drag/sort. Y position stays anchored to the tab container's
    // bottom edge to avoid bouncing during the active-tab bump animation.
    // Erst messen, wenn es überhaupt eine Pille zu setzen gibt: Bei einem
    // einzigen (also aktiven) Tab wäre das ein erzwungener Layout-Durchgang
    // für nichts — und in einer laufenden Bewegung fällt er je Bild an.
    let tabsContainerRect: DOMRect | null = null
    let tabsContainerRead = false
    const readTabsContainer = () => {
      if (!tabsContainerRead) {
        tabsContainerRead = true
        tabsContainerRect = (document.querySelector('.tab-bar__tabs') as HTMLElement | null)?.getBoundingClientRect() ?? null
      }
      return tabsContainerRect
    }

    allTabs.forEach((tab, i) => {
      const pill = container.children[i] as HTMLDivElement
      const isActive = tab.classList.contains('tab-bar__tab--active')
      const isTearingOff = tab.classList.contains('tab-bar__tab--tearing-off')

      if (isActive || isTearingOff) {
        pill.style.display = 'none'
        return
      }

      const rect = computeInactivePillRect(tab, frameRect, readTabsContainer())
      if (!rect) {
        pill.style.display = 'none'
        return
      }

      pill.style.display = ''
      pill.style.left = `${rect.x}px`
      pill.style.top = `${rect.y}px`
      pill.style.width = `${rect.w}px`
      pill.style.height = `${INACTIVE_PILL_HEIGHT}px`

      // Set border-radius from class names (NOT getComputedStyle — that reads
      // mid-transition values and causes the pill to flash rectangular).
      if (tab.classList.contains('tab-bar__tab--left-of-active')) {
        pill.style.borderRadius = '42px 16px 64px 42px'
      } else if (tab.classList.contains('tab-bar__tab--right-of-active')) {
        pill.style.borderRadius = '16px 42px 42px 64px'
      } else {
        pill.style.borderRadius = '42px'
      }
    })
  }, [])

  // Apply path to SVG elements and update gradient position.
  // Does NOT update the pill — the pill must always be at the final tab position,
  // not the interpolated animation position (see animated tab switch below).
  const applyPath = useCallback((content: Rect, tab: Rect | null, isFirst: boolean) => {
    const path = buildUnionPath(content, tab, isFirst)
    bgPathRef.current?.setAttribute('d', path)
    borderPathRef.current?.setAttribute('d', path)
    innerShadowPathRef.current?.setAttribute('d', path)
    innerShadowClipRef.current?.setAttribute('d', path)
    // Position gradient from tab top into content area (bump + 80px bleed)
    if (gradientRef.current && tab) {
      gradientRef.current.setAttribute('y1', String(tab.y))
      gradientRef.current.setAttribute('y2', String(content.y + 80))
    }
  }, [])

  // Immediate path update (skipped during animation).
  // Uses layout position so the bump snaps to the tab's final position even
  // when DnD-Kit FLIP transforms are active (e.g. right after a tab reorder).
  // Bis wann Silhouette und Pillen der Layout-Animation nachgeführt werden.
  const followUntilRef = useRef(0)
  /**
   * Das Rechteck des **Fensterrahmens** — der Bezugspunkt, gegen den alles
   * gerechnet wird.
   *
   * Es ändert sich nur mit dem Fenster, und die Nachführ-Schleife liest es
   * sonst in jedem Bild: einer von drei erzwungenen Layout-Durchgängen je
   * Bild, für einen Wert, der die ganze Bewegung über derselbe ist.
   */
  const frameRectRef = useRef<DOMRect | null>(null)

  useEffect(() => {
    const invalidate = () => { frameRectRef.current = null }
    window.addEventListener('resize', invalidate)
    return () => window.removeEventListener('resize', invalidate)
  }, [])

  const readFrameRect = useCallback((): DOMRect | undefined => {
    if (!frameRectRef.current) {
      const frameEl = document.querySelector('.window-frame, .web-frame')
      frameRectRef.current = frameEl?.getBoundingClientRect() ?? null
    }
    return frameRectRef.current ?? undefined
  }, [])
  // Immer die neueste Fassung, ohne sie in Abhängigkeitslisten zu ziehen:
  // `updatePath` wechselt seine Identität, sobald `isFirstTabActive` kippt —
  // also bei jedem Tab-Wechsel, an dem der erste Tab beteiligt ist. Stünde es
  // unten in den Abhängigkeiten, liefe der Sidebar-Effekt bei genau diesen
  // Wechseln mit und schaltete mit `disablePillTransitions()` die Slide-
  // Animation ab, die dort eigentlich laufen soll.
  const updatePathRef = useRef<() => void>(() => {})

  const updatePath = useCallback(() => {
    if (isAnimatingRef.current) return

    // Der Rahmen einmal, für beide: `measure` und die Pillen messen sonst
    // dasselbe Rechteck zweimal je Durchgang (siehe `measure`). Gemerkt, bis
    // sich das Fenster ändert — siehe `readFrameRect`.
    const frameRect = readFrameRect()

    const m = measure({ useLayoutPosition: true, frameRect })
    if (!m) return

    displayedTabRef.current = m.tab
    applyPath(m.content, m.tab, isFirstTabActive)
    updateTabBgPill(m.tab)
    updateInactiveTabPills(frameRect)
  }, [measure, isFirstTabActive, applyPath, updateTabBgPill, updateInactiveTabPills, readFrameRect])
  updatePathRef.current = updatePath

  // Animated tab switch
  useEffect(() => {
    if (prevTabIdRef.current === undefined) {
      prevTabIdRef.current = activeTabId
      return
    }
    if (prevTabIdRef.current === activeTabId) return
    prevTabIdRef.current = activeTabId

    const from = displayedTabRef.current ? { ...displayedTabRef.current } : null
    if (!from) {
      updatePath()
      return
    }

    isAnimatingRef.current = true

    // Wait one extra frame so React has committed the new .tab-bar__tab--active
    // class before we start measuring. Without this, measure() on web can find
    // the old tab element and animate to the wrong target.
    cancelAnimationFrame(animFrameRef.current)
    animFrameRef.current = requestAnimationFrame(() => {
      const startTime = performance.now()

      // Position pill at the NEW tab immediately — provides gray background
      // while the bump slides over from the old position.
      const initialMeasure = measure()
      if (initialMeasure?.tab) {
        updateTabBgPill(initialMeasure.tab)
      }
      // Sync inactive tab pills (active tab changed)
      updateInactiveTabPills()

      const animate = () => {
        const m = measure()
        if (!m || !m.tab) {
          isAnimatingRef.current = false
          if (m) {
            displayedTabRef.current = m.tab
            applyPath(m.content, m.tab, isFirstTabRef.current)
            updateTabBgPill(m.tab)
          }
          return
        }

        const elapsed = performance.now() - startTime
        const progress = Math.min(elapsed / ANIM_DURATION, 1)
        const t = easeOutCubic(progress)

        const interp = lerpRect(from, m.tab, t)

        displayedTabRef.current = interp
        applyPath(m.content, interp, isFirstTabRef.current)

        if (progress < 1) {
          animFrameRef.current = requestAnimationFrame(animate)
        } else {
          isAnimatingRef.current = false
          displayedTabRef.current = m.tab
          applyPath(m.content, m.tab, isFirstTabRef.current)
          updateTabBgPill(m.tab)
          updateInactiveTabPills()
          // Final layout-position measurement after CSS transitions have settled
          animFrameRef.current = requestAnimationFrame(() => {
            if (isAnimatingRef.current) return
            const final = measure({ useLayoutPosition: true })
            if (final) {
              displayedTabRef.current = final.tab
              applyPath(final.content, final.tab, isFirstTabRef.current)
              updateTabBgPill(final.tab)
              updateInactiveTabPills()
            }
          })
        }
      }

      // Start immediately — no second rAF delay
      animate()
    })

    return () => {
      cancelAnimationFrame(animFrameRef.current)
      isAnimatingRef.current = false
    }
  }, [activeTabId, measure, applyPath, updatePath, updateTabBgPill, updateInactiveTabPills])

  // Update on mount and when dependencies change.
  // The forced re-measure at 300ms must bypass the animation guard so it always
  // applies the correct final width even if the animated switch is still running.
  useEffect(() => {
    updatePath()
    const timer = setTimeout(() => {
      const m = measure({ useLayoutPosition: true })
      if (m) {
        isAnimatingRef.current = false
        displayedTabRef.current = m.tab
        applyPath(m.content, m.tab, isFirstTabRef.current)
        updateTabBgPill(m.tab)
        updateInactiveTabPills()
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [updatePath, activeTabId, sidebarLeftOpen, tabCount, pinnedCount, measure, applyPath, updateTabBgPill, updateInactiveTabPills])

  /**
   * **EIN Beobachter für alle drei Elemente**, die die Kontur bewegen können:
   * die Inhaltskarte (sie wächst, wenn die Navigation weicht — etwa dem
   * geöffneten **Projekt-Canvas**), die Tab-Leiste (Flex-Breite) und das
   * aktive Tab (Titeländerung an Ort und Stelle).
   *
   * Es waren drei getrennte. Sie feuerten bei EINER Layoutänderung, die alle
   * drei betrifft — und das ist der Normalfall, denn sie hängen aneinander —,
   * je einzeln, und jeder Lauf rechnete die volle Kontur samt Pillen neu. Bei
   * einer laufenden Bewegung fiel das je Bild an. Ein Beobachter liefert alle
   * Einträge in EINEM Aufruf; gerechnet wird einmal.
   *
   * Das aktive Tab wird um ein Bild verzögert aufgenommen: Bis dahin hat React
   * die neue Klasse noch nicht ins DOM geschrieben.
   */
  useEffect(() => {
    const observer = new ResizeObserver(() => {
      // **Läuft die Nachführ-Schleife, gehört ihr das Bild.** Sie zieht die
      // Kontur ohnehin in jedem Bild nach (siehe `followUntilRef` weiter
      // unten); ein zweiter Durchgang aus dem Beobachter rechnete dasselbe ein
      // zweites Mal — gemessen acht erzwungene Layout-Durchgänge je Bild statt
      // vier, während der Canvas auf- oder zuging.
      if (performance.now() < followUntilRef.current) return
      try { updatePath() } catch { /* ignore resize observer errors */ }
    })

    const content = contentRef.current
    if (content) observer.observe(content)

    const tabsContainer = document.querySelector('.tab-bar__tabs')
    if (tabsContainer) observer.observe(tabsContainer)

    const rafId = requestAnimationFrame(() => {
      const tabEl = document.querySelector('.tab-bar__tab--active')
      if (tabEl) observer.observe(tabEl)
    })

    return () => {
      cancelAnimationFrame(rafId)
      observer.disconnect()
    }
  }, [contentRef, updatePath, activeTabId, isListMode])

  // Helpers to temporarily disable pill transitions (prevents wobbling during
  // window resize, life space switches, and other rapid layout changes).
  const disablePillTransitions = useCallback(() => {
    const container = inactivePillsRef.current
    if (!container) return
    for (const child of container.children) {
      (child as HTMLElement).classList.remove('content-union-shape__inactive-pill--animated')
    }
  }, [])

  const enablePillTransitions = useCallback(() => {
    // Stay disabled across a window-mode switch settle (the dedicated effect
    // owns re-enabling); otherwise the window-resize handler could re-enable
    // mid-settle and the pills would slide on the next reposition.
    if (suppressPillAnimRef.current) return
    const container = inactivePillsRef.current
    if (!container) return
    for (const child of container.children) {
      (child as HTMLElement).classList.add('content-union-shape__inactive-pill--animated')
    }
  }, [])

  // Window resize — disable pill transitions, re-enable after settle (150ms debounce).
  useEffect(() => {
    let resizeTimer = 0

    const onResize = () => {
      disablePillTransitions()
      updatePath()
      clearTimeout(resizeTimer)
      resizeTimer = window.setTimeout(enablePillTransitions, 150)
    }

    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
      clearTimeout(resizeTimer)
    }
  }, [updatePath, disablePillTransitions, enablePillTransitions])

  // Window-mode switch (list ↔ full) swaps the TabBar in/out of the titlebar slot
  // and resizes the window asynchronously, so the tab geometry is intermediate for
  // a while. Hide the WHOLE bump layer (inactive pills + active union bump/bg) and
  // suppress pill transitions across the settle, then do ONE final measure and
  // reveal — so the union tabs appear directly at their final positions instead of
  // two-stepping / sliding in from the left. Reveal is tied to the actual resize
  // settling (short debounce after the last resize event) with a fallback timeout.
  useEffect(() => {
    if (prevListModeRef.current === isListMode) return // re-ran for another dep, not a real switch (also skips mount)
    prevListModeRef.current = isListMode

    suppressPillAnimRef.current = true
    disablePillTransitions()
    if (bumpSlotEl) bumpSlotEl.style.visibility = 'hidden'

    let settleTimer = 0
    let fallbackTimer = 0
    let unhidden = false
    // Reveal the bump layer once the resize has settled, with a final snap into
    // place. Transitions stay suppressed (re-enabled later) so any straggler
    // re-measure (e.g. the active tab settling its width) snaps too, never animates.
    const unhide = () => {
      if (unhidden) return
      unhidden = true
      window.removeEventListener('resize', onResize)
      clearTimeout(settleTimer)
      clearTimeout(fallbackTimer)
      const m = measure({ useLayoutPosition: true })
      if (m) {
        isAnimatingRef.current = false
        displayedTabRef.current = m.tab
        applyPath(m.content, m.tab, isFirstTabRef.current)
        updateTabBgPill(m.tab)
        updateInactiveTabPills()
      }
      if (bumpSlotEl) bumpSlotEl.style.visibility = ''
    }
    const onResize = () => {
      clearTimeout(settleTimer)
      settleTimer = window.setTimeout(unhide, 120)
    }

    // Web: no window resize follows the mode switch (pure CSS swap), so the
    // layout is already final — waiting for a settle would just leave the bump
    // hidden until the 700ms fallback. Measure + reveal right away; keep pill
    // transitions suppressed briefly so pills snap into place, never slide.
    if (!isElectron) {
      unhide()
      const reEnableTimer = window.setTimeout(() => {
        suppressPillAnimRef.current = false
        enablePillTransitions()
      }, 150)
      return () => {
        clearTimeout(reEnableTimer)
        suppressPillAnimRef.current = false
        if (bumpSlotEl) bumpSlotEl.style.visibility = ''
      }
    }

    window.addEventListener('resize', onResize)
    fallbackTimer = window.setTimeout(unhide, 700)

    // Re-enable pill transitions only after everything has definitely settled
    // (past any late re-measure), so the last pill snaps rather than animates.
    const reEnableTimer = window.setTimeout(() => {
      suppressPillAnimRef.current = false
      enablePillTransitions()
    }, 900)

    return () => {
      window.removeEventListener('resize', onResize)
      clearTimeout(settleTimer)
      clearTimeout(fallbackTimer)
      clearTimeout(reEnableTimer)
      // Never leave the layer hidden / transitions suppressed if we re-switch or unmount mid-settle.
      suppressPillAnimRef.current = false
      if (bumpSlotEl) bumpSlotEl.style.visibility = ''
    }
  }, [isListMode, isElectron, bumpSlotEl, disablePillTransitions, enablePillTransitions, measure, applyPath, updateTabBgPill, updateInactiveTabPills])

  // Life space switch — when tab count changes, the entire tab set is replaced.
  // Disable pill transitions so pills snap to new positions without wobbling.
  useEffect(() => {
    if (prevTabCountRef.current !== tabCount) {
      prevTabCountRef.current = tabCount
      disablePillTransitions()
      const timer = setTimeout(enablePillTransitions, 150)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [tabCount, disablePillTransitions, enablePillTransitions])

  // Sidebar toggle — disable pill transitions so pills snap to new positions
  // without wobbling during the sidebar CSS transition, and TRACK the tabs while
  // that transition runs.
  //
  // Das Nachführen ist der eigentliche Punkt: diese Ebene wird von drei
  // ResizeObservern angestoßen, und die reagieren ausschließlich auf GRÖSSEN.
  // Beim Ein- und Ausblenden der Navigation verschieben sich die Tabs aber bloß
  // — der Slot in der Titlebar wächst um die Breite der Sidebar, die Tabs darin
  // bleiben exakt gleich breit. Kein Observer feuert also, und Silhouette wie
  // Pillen blieben stehen, während Beschriftung und Symbol darüber davonwandern.
  // Deshalb hier eine Schleife über die Dauer des Übergangs, wie sie der
  // Fenster-Resize-Handler weiter oben mit seinem `updatePath()` bereits hat.
  // `useLayoutEffect`, nicht `useEffect`: der Klassenwechsel und die neue
  // Tab-Position landen in EINEM Commit, ein Effekt nach dem Malen käme also
  // einen Frame zu spät — sichtbar als kurzes Nachspringen zwischen Silhouette
  // und Beschriftung. Hier wird noch vor dem Malen einmal nachgezogen, danach
  // übernimmt die Schleife.
  useLayoutEffect(() => {
    disablePillTransitions()
    // Der Rahmen kann sich mit demselben Anlass geändert haben (Fenstermodus).
    frameRectRef.current = null
    followUntilRef.current = performance.now() + FOLLOW_MS
    updatePathRef.current()

    let rafId = requestAnimationFrame(function follow() {
      updatePathRef.current()
      rafId = requestAnimationFrame(follow)
    })

    const timer = setTimeout(() => {
      cancelAnimationFrame(rafId)
      followUntilRef.current = 0
      enablePillTransitions()
    }, FOLLOW_MS)

    return () => {
      cancelAnimationFrame(rafId)
      clearTimeout(timer)
    }
  }, [sidebarLeftOpen, sidebarRightOpen, disablePillTransitions, enablePillTransitions])

  // Nachzügler-Commits während desselben Frames.
  //
  // Layout-Effekte laufen von INNEN NACH AUSSEN: der Effekt oben feuert also,
  // bevor MainLayout der Titlebar den neuen Sidebar-Zustand gibt. Die Titlebar
  // hängt nicht am JSX von MainLayout, ihr Zustand kommt über den Context — und
  // dessen `setState` aus einem Layout-Effekt schiebt React als zusätzlichen,
  // synchronen Durchlauf nach, noch vor dem Malen. Erst DORT steht die neue
  // Breite der linken Bedienelemente im DOM und damit die endgültige
  // Tab-Position.
  //
  // Bewusst ohne Abhängigkeitsliste: dieser Effekt soll jeden Commit mitnehmen,
  // nicht nur den ersten. Das Zeitfenster begrenzt ihn auf den Übergang.
  useLayoutEffect(() => {
    if (performance.now() < followUntilRef.current) updatePathRef.current()
  })

  // Follow active tab during drag (bump tracks X position).
  // Separate handlers for move vs end: move uses visual position (getBoundingClientRect)
  // so the bump follows the dragged tab; end uses layout position (offsetLeft) so the
  // bump snaps to the tab's final position even during DnD-Kit FLIP animations.
  // Cancels any ongoing tab-switch animation — drag/cancel events take priority.
  useEffect(() => {
    let moveRafId = 0
    let endRafId = 0
    // Track whether a drag is currently active so we can disable pill
    // transitions exactly once on drag-start and re-enable on drag-end.
    // Without this, the pill's 0.35s transition lags behind the dragged or
    // FLIP'd tab — the tab visually moves but its pill chases and the user
    // sees mismatched, displaced pills underneath each tab.
    let dragActive = false

    const cancelAnimation = () => {
      if (isAnimatingRef.current) {
        cancelAnimationFrame(animFrameRef.current)
        isAnimatingRef.current = false
      }
    }

    const onDragMove = () => {
      if (!dragActive) {
        dragActive = true
        disablePillTransitions()
      }
      if (moveRafId) return
      moveRafId = requestAnimationFrame(() => {
        moveRafId = 0
        cancelAnimation()
        const m = measure()
        if (!m) return
        displayedTabRef.current = m.tab
        const tabEl = document.querySelector('.tab-bar__tab--active') as HTMLElement | null
        const isFirst = tabEl?.classList.contains('tab-bar__tab--first-active') ?? isFirstTabRef.current
        applyPath(m.content, m.tab, isFirst)
        updateTabBgPill(m.tab)
        updateInactiveTabPills()
      })
    }

    const onDragEnd = () => {
      dragActive = false
      if (endRafId) return
      endRafId = requestAnimationFrame(() => {
        endRafId = 0
        cancelAnimation()
        // Layout position: immune to DnD-Kit FLIP transforms after reorder
        const m = measure({ useLayoutPosition: true })
        if (!m) return
        displayedTabRef.current = m.tab
        const tabEl = document.querySelector('.tab-bar__tab--active') as HTMLElement | null
        const isFirst = tabEl?.classList.contains('tab-bar__tab--first-active') ?? isFirstTabRef.current
        applyPath(m.content, m.tab, isFirst)
        updateTabBgPill(m.tab)
        updateInactiveTabPills()
        // Re-enable transitions on the next frame so the just-set positions
        // settle before the CSS transition rule comes back.
        requestAnimationFrame(() => enablePillTransitions())
      })
    }

    const unsubMove = onAppEvent('tab-drag-move', onDragMove)
    const unsubEnd = onAppEvent('tab-drag-end', onDragEnd)
    return () => {
      cancelAnimationFrame(moveRafId)
      cancelAnimationFrame(endRafId)
      unsubMove()
      unsubEnd()
    }
  }, [measure, applyPath, updateInactiveTabPills, updateTabBgPill, disablePillTransitions, enablePillTransitions])

  // Re-measure after sidebar transition (200ms CSS transition)
  useEffect(() => {
    const timer = setTimeout(updatePath, 220)
    return () => clearTimeout(timer)
  }, [sidebarLeftOpen, updatePath])

  // Cleanup animation on unmount
  useEffect(() => {
    return () => cancelAnimationFrame(animFrameRef.current)
  }, [])

  // Split rendering: bump layers (pill, bg, inner-shadow) go into the bump-slot
  // BEFORE the titlebar in DOM order. Border goes into the border-slot AFTER content.
  // DOM-order stacking: bump (1st) < titlebar/labels (2nd) < content (3rd) < border (4th).

  const bumpContent = (
    <div className="content-union-shape content-union-shape--bump">
      {/* Inactive tab background pills: positioned before bump SVG so bump covers them */}
      <div ref={inactivePillsRef} className="content-union-shape__inactive-pills" />
      {/* Active tab background pill: gray fill, covered by bump SVG (DOM order within layer) */}
      <div ref={tabBgPillRef} className="content-union-shape__tab-bg-pill" />
      {/* Background layer: white fill + drop-shadow via SVG <filter> */}
      <svg className={`content-union-shape__bg${isPreview ? ' content-union-shape__bg--preview' : ''}`} width="100%" height="100%">
        <defs>
          <linearGradient ref={gradientRef} id="preview-bump-gradient" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="0" y2="100">
            <stop offset="0%" className="content-union-shape__gradient-start" />
            <stop offset="100%" className="content-union-shape__gradient-end" />
          </linearGradient>
          {/* SVG drop shadow equivalent to CSS drop-shadow(0 1px 3px rgba(0,0,0,0.1)) drop-shadow(0 1px 1px rgba(0,0,0,0.06)) */}
          <filter id="union-shadow" colorInterpolationFilters="sRGB" x="-5%" y="-5%" width="110%" height="110%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="1.5" result="blur1" />
            <feOffset in="blur1" dy="1" result="off1" />
            <feFlood floodColor="black" floodOpacity="0.1" result="color1" />
            <feComposite in="color1" in2="off1" operator="in" result="shadow1" />
            <feGaussianBlur in="SourceAlpha" stdDeviation="0.5" result="blur2" />
            <feOffset in="blur2" dy="1" result="off2" />
            <feFlood floodColor="black" floodOpacity="0.06" result="color2" />
            <feComposite in="color2" in2="off2" operator="in" result="shadow2" />
            <feMerge>
              <feMergeNode in="shadow1" />
              <feMergeNode in="shadow2" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <path ref={bgPathRef} filter="url(#union-shadow)" />
      </svg>
      {/* Inner shadow layer: clipped white stroke creates inset border effect */}
      <svg className="content-union-shape__inner-shadow" width="100%" height="100%">
        <defs>
          <clipPath id="inner-shadow-clip">
            <path ref={innerShadowClipRef} />
          </clipPath>
        </defs>
        <path ref={innerShadowPathRef} clipPath="url(#inner-shadow-clip)" />
      </svg>
    </div>
  )

  const borderContent = (
    <div className="content-union-shape content-union-shape--border-container">
      <svg className={`content-union-shape__border${isPreview ? ' content-union-shape__border--preview' : ''}`} width="100%" height="100%">
        <path ref={borderPathRef} />
      </svg>
    </div>
  )

  return (
    <>
      {bumpSlotEl && createPortal(bumpContent, bumpSlotEl)}
      {borderSlotEl && createPortal(borderContent, borderSlotEl)}
    </>
  )
}

export const ContentUnionShape = React.memo(ContentUnionShapeComponent)
